import React from "react";
import { getFlowStatus, formatTimeRemaining } from './warmupFlowUtils';
import { FLOW_END_CONFIG } from './FlowSelectModal';
import { STRIKETHROUGH_TAGS } from '../shared/enums';

function getStepIcon(currentStep) {
  if (currentStep?.nodeType === 'condition') return 'fa fa-question';
  if (currentStep?.nodeType === 'split') return 'fa fa-code-branch';
  if (currentStep?.nodeType === 'delay') return 'fa fa-hourglass-half';
  if (currentStep?.nodeType === 'end') {
    const endType = currentStep?.endType || 'completed';
    const endIcons = {
      completed: 'fa fa-check-circle', converted: 'fa fa-trophy',
      disqualified: 'fa fa-ban', not_interested: 'fa fa-times-circle',
      wrong_contact: 'fa fa-exclamation-triangle', nurture: 'fa fa-clock',
    };
    return endIcons[endType] || 'fa fa-flag-checkered';
  }
  if (currentStep?.type === 'email_sent') return 'fa fa-envelope';
  if (currentStep?.type?.includes('instagram')) return 'fab fa-instagram';
  if (currentStep?.type?.includes('linkedin')) return 'fab fa-linkedin';
  if (currentStep?.type?.includes('whatsapp')) return 'fab fa-whatsapp';
  if (currentStep?.type === 'loom_video') return 'fa fa-video';
  if (currentStep?.type === 'webinar') return 'fa fa-desktop';
  if (currentStep?.type === 'lead_magnet') return 'fa fa-magnet';
  if (currentStep?.type === 'follow_up') return 'fa fa-redo';
  return 'fa fa-fire';
}

function getStepBadgeTheme(currentStep) {
  const type = currentStep?.type;
  if (type?.includes('instagram')) return 'badge-instagram';
  if (type?.includes('linkedin')) return 'badge-linkedin';
  if (type?.includes('whatsapp')) return 'badge-whatsapp';
  if (type?.includes('email') || type === 'email_sent') return 'badge-email';
  if (type?.includes('twitter')) return 'badge-twitter';
  if (type?.includes('facebook')) return 'badge-facebook';
  if (type?.includes('phone') || type?.includes('call')) return 'badge-phone';
  if (currentStep?.nodeType === 'condition') return 'badge-condition';
  if (currentStep?.nodeType === 'delay') return 'badge-delay';
  return '';
}

export function getFlowStepTheme(rowData, warmupFlows) {
  const flowStatus = getFlowStatus(rowData, warmupFlows, 'warmup');
  if (!flowStatus) return '';
  let step = flowStatus.currentStep;
  if (step?.nodeType === 'delay' && flowStatus.isReady) {
    const actionStep = findNextStepFromEdges(flowStatus.flow, step);
    if (actionStep) step = actionStep;
  }
  const type = step?.type;
  if (type?.includes('instagram')) return 'cell-theme-instagram';
  if (type?.includes('linkedin')) return 'cell-theme-linkedin';
  return '';
}

const findNextStepFromEdges = (flow, step) => {
  if (!step?.id || !flow?.visualData?.edges) return null;
  const outgoing = flow.visualData.edges.filter(e => e.source === step.id);
  if (outgoing.length === 0) return null;
  return flow.steps?.find(s => s.id === outgoing[0].target) || null;
};

const hasNegativeTag = (tags) => {
  if (!tags) return false;
  return STRIKETHROUGH_TAGS.some(tagId => tags[tagId]) ||
    Object.keys(tags).some(key =>
      STRIKETHROUGH_TAGS.includes(key.toLowerCase().replace(/\s+/g, '_'))
    );
};

const reasonLabel = (reason) => FLOW_END_CONFIG[reason]?.label
  || (reason ? reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Ended');

const WarmupFlowBadge = ({ rowData, rowId, warmupFlows, completingFlowStep, onOpenFlowSelect }) => {
  const flowStatus = getFlowStatus(rowData, warmupFlows, 'warmup');
  const isDisqualified = rowData.isDisqualified;
  const flowEndStatus = rowData.flowEndStatus;
  const isNegative = isDisqualified || hasNegativeTag(rowData.tags);

  if (!flowStatus && flowEndStatus) {
    const effectiveStatus = isNegative && flowEndStatus === 'completed' ? 'not_interested' : flowEndStatus;
    const endConfig = FLOW_END_CONFIG[effectiveStatus] || FLOW_END_CONFIG.completed;
    // Every entry in warmupFlowHistory represents a flow that already ran to its end,
    // regardless of the specific outcome — show all of them, most recent first.
    const finishedFlows = rowData.warmupFlowHistory || [];
    const lastFlow = finishedFlows[finishedFlows.length - 1];
    const extraCount = finishedFlows.length - 1;
    const tooltip = finishedFlows.length > 0
      ? finishedFlows.map(h => `${h.flowName || 'Flow'} — ${reasonLabel(h.reason)}`).join('\n')
      : endConfig.label;

    return (
      <button
        className={`warmup-flow-badge ended ${effectiveStatus}`}
        onClick={(e) => { e.stopPropagation(); onOpenFlowSelect(rowId); }}
        title={tooltip}
        style={{ '--end-color': endConfig.color }}
      >
        <span className="flow-end-icon">
          {endConfig.icon
            ? <i className={endConfig.icon} style={{ color: endConfig.color }}></i>
            : <i className="fa fa-flag-checkered" style={{ color: endConfig.color }}></i>}
        </span>
        <span className="flow-end-info">
          <span className="flow-end-name">{lastFlow?.flowName || endConfig.label}</span>
          <span className="flow-end-sub" style={{ color: endConfig.color }}>{endConfig.text || endConfig.label}</span>
        </span>
        {extraCount > 0 && <span className="flow-end-count" title={`${finishedFlows.length} finished flows`}>+{extraCount}</span>}
      </button>
    );
  }

  if (!flowStatus) {
    if (warmupFlows.length === 0) {
      return (
        <span className="warmup-flow-badge empty" title="No warmup flows configured">
          <i className="fa fa-minus"></i>
        </span>
      );
    }
    return (
      <button
        className={`warmup-flow-badge start-flow ${isDisqualified ? 'disabled' : ''}`}
        onClick={(e) => { e.stopPropagation(); if (!isDisqualified) onOpenFlowSelect(rowId); }}
        title={isDisqualified ? "Contact is disqualified" : "Start Warmup Flow"}
        disabled={isDisqualified}
      >
        <i className="fa fa-bolt"></i>
      </button>
    );
  }

  const { currentStep, isReady, timeRemaining, isManualCondition, flow } = flowStatus;

  // Skip past ready delay nodes to show the actual actionable step
  let displayStep = currentStep;
  if (currentStep?.nodeType === 'delay' && isReady) {
    const actionStep = findNextStepFromEdges(flow, currentStep);
    if (actionStep) displayStep = actionStep;
  }

  const stepLabel = displayStep?.type || displayStep?.nodeType || 'Step';

  return (
    <div className="warmup-flow-status-cell">
      <button
        className={`warmup-flow-badge active ${isReady ? 'ready' : 'waiting'} ${getStepBadgeTheme(displayStep)} ${completingFlowStep[rowId] ? 'completing' : ''}`}
        onClick={(e) => { e.stopPropagation(); onOpenFlowSelect(rowId); }}
        title={`${flow.name}: ${stepLabel}${!isReady ? ` (${formatTimeRemaining(timeRemaining)})` : ''}`}
      >
        <i className={getStepIcon(displayStep)}></i>
        {isReady && !isManualCondition && <span className="flow-ready-dot"></span>}
      </button>
    </div>
  );
};

export default WarmupFlowBadge;
